import { clamp } from 'lodash';
import * as PIXI from 'pixi.js';
import { TOP_LAYER_ZINDEX } from '../../constants/Layers';
import Color from '../Color';
import AlignmentGuides from './AlignmentGuides';
import Handle from './Handle';

// A 720px tall renderer will display handles with a radius of 15.
// This ensures consistent sizing of handles
const HANDLE_RADIUS_HEIGHT_RATIO = 25 / 720;

export default class Draggable {
  static activeDraggable = null;

  constructor({
    app,
    contents,
    anchor,
  }) {
    this.app = app;
    this.contents = contents;
    this.anchor = anchor ?? { x: 0, y: 0 };

    this.scale = 1;

    this.scaling = false;
    this.moving = false;
    this.dragged = false;

    // Prepare the container that will hold our graphic overlays.
    // This container will be resized.
    this.container = new PIXI.Container();
    this.container.interactive = true;
    this.container.cursor = 'move';
    this.container.width = this.app.screen.width;
    this.container.height = this.app.screen.height;
    this.container.zIndex = TOP_LAYER_ZINDEX;

    this.alignmentGuides = AlignmentGuides.getInstance(app);

    // Setup events
    this.container
      .on('mouseover', this._onMouseOver)
      .on('mouseout', this._onMouseOut)
      .on('mousedown', this._onDragStart)
      .on('touchstart', this._onDragStart)
      .on('mouseup', this._onDragEnd)
      .on('mouseupoutside', this._onDragEnd)
      .on('touchend', this._onDragEnd)
      .on('touchendoutside', this._onDragEnd)
      .on('mousemove', this._onDragMove)
      .on('touchmove', this._onDragMove)
      .on('click', this._onClick);

    // Setup handles
    this.handleTL = this.createHandle('nwse-resize');
    this.handleTR = this.createHandle('nesw-resize');
    this.handleBR = this.createHandle('nwse-resize');
    this.handleBL = this.createHandle('nesw-resize');

    this.handles = [this.handleTL, this.handleTR, this.handleBR, this.handleBL];

    // Outline turns off/on, hit area is always "visible"
    this.outline = new PIXI.Graphics();
    this.outline.blendMode = PIXI.BLEND_MODES.OVERLAY;

    this.hitarea = new PIXI.Graphics();

    this.container.addChild(this.hitarea, this.outline, ...this.handles);

    this.hideControls();
  }

  turnOffInteraction() {
    this.container.interactive = false;
  }

  hideControls() {
    this.hideHandles();
    this.outline.renderable = false;
  }

  hideHandles() {
    this.handles.forEach((handle) => {
      handle.renderable = false;
    });
  }

  showControls() {
    this.showHandles();
    this.outline.renderable = true;
  }

  showHandles() {
    this.handles.forEach((handle) => {
      handle.renderable = true;
    });
  }

  createHandle(cursor) {
    const texture = Handle.makeTexture(
      this.app.renderer,
      HANDLE_RADIUS_HEIGHT_RATIO * this.app.renderer.height
    );
    return new Handle(texture, cursor);
  }

  /**
   * Uses the position and bounds to determine where
   * and how large the outline and interaction area
   * should be.
   *
   * @param bounds {PIXI.Rectangle}
   */
  update(bounds) {
    this.bounds = bounds;
    this.render();
  }

  render() {
    this.hitarea.clear();
    this.outline.clear();

    // Alpha 0.001 fills the hit area to make it interactive without showing a visible box.
    this.hitarea
      .beginFill(Color.toNumber(Color.BLACK), 0.001)
      .drawShape(this.bounds);

    this.positionHandles(this.bounds);
    this.drawOutline(this.bounds);
  }

  positionHandles(bounds) {
    this.handleTL.x = bounds.x;
    this.handleTL.y = bounds.y;
    this.handleTR.x = bounds.x + bounds.width;
    this.handleTR.y = bounds.y;
    this.handleBR.x = bounds.x + bounds.width;
    this.handleBR.y = bounds.y + bounds.height;
    this.handleBL.x = bounds.x;
    this.handleBL.y = bounds.y + bounds.height;
  }

  drawOutline(bounds, borderColor = Color.BLUE, borderSize = 2) {
    this.outline
      .clear()
      .lineStyle(borderSize, Color.toNumber(borderColor))
      .drawShape(bounds);
  }

  onStart = () => {};
  onEnd = () => {};
  onDrag = () => {};
  onClick = () => {};
  safeScale = (scale) => scale;

  _onClick = (event) => {
    // Click always fires last in the "mouseup" type events
    // This is cleaner than needing to turn the event off and on
    if (this.dragged) {
      this.dragged = false;
    } else {
      this.onClick(event);
    }
  };

  _onDragStart = (event) => {
    this.startEvent = event;
    Draggable.activeDraggable = this;

    if (this.handles.includes(event.target)) {
      this.scaling = true;

      this.activeHandle = event.target;

      if (this.anchor.x === 0 && this.anchor.y === 0) {
        this.setupResizeFromCorners();
      }
      // Always show controls while scaling.
      this.showControls();
      this.onStart();
    } else if (event.target === this.container) {
      this.moving = true;
      this.hideHandles();

      const position = this.startEvent.data.getLocalPosition(this.app.stage);
      this.cursorOffset = {
        x: position.x - this.contents.x - this.anchor.x * this.contents.width,
        y: position.y - this.contents.y - this.anchor.y * this.contents.height,
      };
      this.onStart();
    }
  };

  _onDragEnd = (event) => {
    if (
      this.scaling &&
      this.anchor.x === 0 &&
      this.anchor.y === 0
    ) {
      this.teardownResizeFromCorners();
    }

    this.onEnd(this.moving, this.scaling);
    if (
      this.scaling &&
      (event.type === 'mouseupoutside' || event.type === 'touchendoutside')
    ) {
      // Hide controls so they dont get stuck visible after scaling.
      this.hideControls();
    }
    if (this.moving) {
      this.showHandles();
    }
    this.moving = false;
    this.scaling = false;
    this.startEvent = null;
    this.activeHandle = null;
    Draggable.activeDraggable = null;
    this.alignmentGuides.hideAll();
  };

  _onDragMove = () => {
    if (this.scaling || this.moving) {
      const position = this.startEvent.data.getLocalPosition(this.app.stage);

      if (this.scaling) this.handleScale(position);
      if (this.moving) this.handleMove(position);

      this.alignmentGuides.toggleShowHideFor(
          this.contents.getBounds(),
          this.scaling
      );

      this.dragged = true;
      this.onDrag();
    }
  };

  /**
   * @param position {PIXI.Point}
   */
  handleScale(position) {
    const unscaledWidth = this.contents.width / this.contents.scale.x;
    const unscaledHeight = this.contents.height / this.contents.scale.y;
    const { x, y } = this.contents;

    const xDirection =
      this.activeHandle === this.handleTR || this.activeHandle === this.handleBR
        ? 1
        : -1;
    const yDirection =
      this.activeHandle === this.handleBR || this.activeHandle === this.handleBL
        ? 1
        : -1;

    const newWidth = ((position.x - x) / (1 - this.anchor.x)) * xDirection;
    const newHeight = ((position.y - y) / (1 - this.anchor.y)) * yDirection;

    const scaleX = this.safeScale(newWidth / unscaledWidth);
    const scaleY = this.safeScale(newHeight / unscaledHeight);

    this.contents.scale.set(Math.max(scaleX, scaleY));

    // Edit the scale based on snapping to alignment guides
    const snappedScale = this.alignmentGuides.snapToGuidesByScale(
        this.contents,
        this.anchor
    );
    this.contents.scale.set(this.safeScale(snappedScale));
  }

  /**
   * @param position {PIXI.Point}
   */
  handleMove(position) {
    this.contents.x = position.x - this.cursorOffset.x;
    this.contents.y = position.y - this.cursorOffset.y;

    const { snappedX, snappedY } =
        this.alignmentGuides.snapToGuidesByPosition(
            this.contents.getBounds(),
            this.anchor
        );
    this.contents.x = snappedX;
    this.contents.y = snappedY;
  }

  _onMouseOver = () => {
    if (Draggable.activeDraggable === null) {
      // This update is a bandaid that helps handle the issue with extra space above/below text
      this.update(this.contents.getBounds());

      this.showControls();
    }
  };

  _onMouseOut = () => {
    // Don't hide while scaling or moving, even if mouse leaves the bounding box.
    // Prevents controls from disappearing if the user moves the mouse very quickly.
    if (Draggable.activeDraggable !== this) {
      this.hideControls();
    }
  };

  confineContentsToCanvas(newX, newY) {
    // Allow a large display object to scroll over the stage when its aspect
    // ratio differs from the stage's aspect ratio, without showing any of the
    // blank stage behind it. E.g. landscape bg image over 1:1 stage.
    const x = clamp(newX, this.app.screen.width - this.contents.width, 0);
    const y = clamp(newY, this.app.screen.height - this.contents.height, 0);
    return { x, y };
  }

  setupResizeFromCorners() {
    const unscaledWidth = this.contents.width / this.contents.scale.x;
    const unscaledHeight = this.contents.height / this.contents.scale.y;

    if (this.activeHandle === this.handleTL) {
      this.contents.pivot.set(unscaledWidth, unscaledHeight);
      this.contents.x += this.contents.width;
      this.contents.y += this.contents.height;
    } else if (this.activeHandle === this.handleTR) {
      this.contents.pivot.set(0, unscaledHeight);
      this.contents.y += this.contents.height;
    } else if (this.activeHandle === this.handleBR) {
      this.contents.pivot.set(0, 0);
    } else if (this.activeHandle === this.handleBL) {
      this.contents.pivot.set(unscaledWidth, 0);
      this.contents.x += this.contents.width;
    }
  }

  teardownResizeFromCorners() {
    this.contents.pivot.set(0, 0);
    if (this.activeHandle === this.handleTL) {
      this.contents.x -= this.contents.width;
      this.contents.y -= this.contents.height;
    } else if (this.activeHandle === this.handleTR) {
      this.contents.y -= this.contents.height;
    } else if (this.activeHandle === this.handleBR) {
      // nothing
    } else if (this.activeHandle === this.handleBL) {
      this.contents.x -= this.contents.width;
    }
  }
}
